/**
 * TimeField — an iOS-style wheel/spinner time picker in a popover.
 *
 * The trigger (styled like DateField) opens two scrolling columns — hours
 * (`minHour`..`maxHour`, default 9–18) and minutes (00–59). The centre row is
 * the selection: it sits under a highlight band, rendered with a slightly
 * heavier weight, while neighbours fade by distance. Scrolling snaps to the nearest item on both web and native;
 * tapping a value scrolls it to the centre. Minutes are per-minute, so an exact
 * time like 09:03 is reachable without typing.
 *
 * Snap strategy differs by platform: native leans on `snapToInterval` +
 * `onMomentumScrollEnd`; web (where those don't fire reliably) commits on a
 * debounced scroll-stop and animates the column to the centred offset.
 *
 * Outside-click dismissal mirrors LanguageMenu / DateField: a full-bleed
 * Backdrop (`fixed` on web, `absolute` on native) under the absolute panel.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Platform, ScrollView, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native'
import { Stack, Text, XStack, YStack } from 'tamagui'

import { pad2 } from '../lib/date'
import { palette } from '../theme/palette'
import { Backdrop } from './Backdrop'
import { Icon } from './Icon'

const isWeb = Platform.OS === 'web'

const ITEM_H = 36
const VISIBLE = 5 // odd → one centred row with two neighbours each side
const PAD = ((VISIBLE - 1) / 2) * ITEM_H // top/bottom spacer so edges can centre
const VIEW_H = VISIBLE * ITEM_H

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n))
const range = (a: number, b: number): number[] => Array.from({ length: b - a + 1 }, (_, i) => a + i)

/** Parse `HH:MM` into clamped [hour, minute]; falls back to [minHour, 0]. */
function parseHM(s: string, minHour: number, maxHour: number): [number, number] {
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(s.trim())
  if (!m) return [minHour, 0]
  const h = clamp(Number(m[1]) || 0, minHour, maxHour)
  const mi = clamp(Number(m[2]) || 0, 0, 59)
  return [h, mi]
}

/** One scrolling column of zero-padded numbers; reports the centred value. */
function WheelColumn({
  values,
  value,
  onChange,
  width = 60,
}: {
  values: number[]
  value: number
  onChange: (next: number) => void
  width?: number
}) {
  const ref = useRef<ScrollView>(null)
  const idxOf = (v: number) => {
    const i = values.indexOf(v)
    return i < 0 ? 0 : i
  }
  const [center, setCenter] = useState(() => idxOf(value))
  const selfIdx = useRef(idxOf(value)) // last index we committed — guards the sync effect
  const settle = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Centre the initial value once the column has mounted (popover just opened).
  useEffect(() => {
    const i = idxOf(value)
    const id = setTimeout(() => ref.current?.scrollTo({ y: i * ITEM_H, animated: false }), 0)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-centre only when the value changes from OUTSIDE this column.
  useEffect(() => {
    const i = idxOf(value)
    if (i === selfIdx.current) return
    selfIdx.current = i
    setCenter(i)
    const id = setTimeout(() => ref.current?.scrollTo({ y: i * ITEM_H, animated: false }), 0)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // unmount во время инерционного скролла на вебе — settle.current иначе стрельнёт commit() позже
  useEffect(() => {
    return () => {
      if (settle.current) clearTimeout(settle.current)
    }
  }, [])

  const commit = (offsetY: number, snap: boolean) => {
    const i = clamp(Math.round(offsetY / ITEM_H), 0, values.length - 1)
    selfIdx.current = i
    setCenter(i)
    if (values[i] !== value) onChange(values[i])
    if (snap && Math.abs(offsetY - i * ITEM_H) > 1) ref.current?.scrollTo({ y: i * ITEM_H, animated: true })
  }

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const y = e.nativeEvent.contentOffset.y
    setCenter(clamp(Math.round(y / ITEM_H), 0, values.length - 1)) // live fade
    if (isWeb) {
      if (settle.current) clearTimeout(settle.current)
      settle.current = setTimeout(() => commit(y, true), 110) // web: snap on scroll-stop
    }
  }

  return (
    <ScrollView
      ref={ref}
      style={{ width, height: VIEW_H }}
      showsVerticalScrollIndicator={false}
      scrollEventThrottle={16}
      onScroll={onScroll}
      onMomentumScrollEnd={(e) => commit(e.nativeEvent.contentOffset.y, !isWeb)}
      snapToInterval={ITEM_H}
      decelerationRate="fast"
      // CSS scroll-snap is web-only; harmless/ignored on native.
      contentContainerStyle={isWeb ? ({ scrollSnapType: 'y mandatory' } as object) : undefined}
    >
      <Stack height={PAD} />
      {values.map((v, i) => {
        const dist = Math.abs(i - center)
        const opacity = dist === 0 ? 1 : dist === 1 ? 0.45 : dist === 2 ? 0.2 : 0.08
        return (
          <Stack
            key={v}
            height={ITEM_H}
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            onPress={() => commit(i * ITEM_H, true)}
            // center each row for CSS scroll-snap on web
            style={isWeb ? ({ scrollSnapAlign: 'center' } as object) : undefined}
            role="button"
          >
            <Text fontSize={dist === 0 ? 16 : 14} fontWeight={dist === 0 ? '600' : '400'} color="$color" opacity={opacity}>
              {pad2(v)}
            </Text>
          </Stack>
        )
      })}
      <Stack height={PAD} />
    </ScrollView>
  )
}

export interface TimeFieldProps {
  /** Selected value as `HH:MM`, or '' when empty. */
  value: string
  onChange: (next: string) => void
  /** Hour range offered by the wheel (inclusive). Default 9–18. */
  minHour?: number
  maxHour?: number
  placeholder?: string
  /** Locked, read-only — shows the value but never opens the wheel. */
  disabled?: boolean
  /** Highlight as a still-empty required field — navy accent border until filled. */
  highlight?: boolean
  /** Exclusive lower bound as `HH:MM` — the wheel won't offer any time ≤ this. */
  after?: string
  /** Horizontal anchor of the panel relative to the field (mirrors DateField).
   *  Use `right` when the field sits in a row's right column so the wheel opens
   *  leftward instead of overflowing the screen edge on mobile. */
  align?: 'left' | 'right'
}

export function TimeField({
  value,
  onChange,
  minHour = 9,
  maxHour = 18,
  placeholder = 'ЧЧ:ММ',
  disabled,
  highlight,
  after,
  align = 'left',
}: TimeFieldProps) {
  const [open, setOpen] = useState(false)
  const [h, m] = parseHM(value, minHour, maxHour)

  // Optional exclusive lower bound (`after`, `HH:MM`): the wheel must not OFFER
  // any time ≤ it. Trim whole hours that fall under the bound, and raise the
  // minutes floor for the bound's own hour — earlier times become unscrollable.
  const bound = useMemo(() => (after ? parseHM(after, minHour, maxHour) : null), [after, minHour, maxHour])
  const hours = useMemo(() => {
    const all = range(minHour, maxHour)
    const kept = bound ? all.filter((hh) => hh > bound[0] || (hh === bound[0] && bound[1] < 59)) : all
    return kept.length ? kept : all // never leave the column empty (degenerate bound)
  }, [minHour, maxHour, bound])
  const minuteFloor = bound && h === bound[0] ? bound[1] + 1 : 0
  const minutes = useMemo(() => {
    const r = range(minuteFloor, 59)
    return r.length ? r : range(0, 59)
  }, [minuteFloor])

  return (
    <Stack position="relative">
      {/* trigger — styled to match the form Inputs (height 40, $bgLayout chrome) */}
      <XStack
        onPress={disabled ? undefined : () => setOpen((o) => !o)}
        cursor={disabled ? 'default' : 'pointer'}
        height={40}
        alignItems="center"
        gap={8}
        paddingHorizontal={11}
        backgroundColor="$bgLayout"
        borderWidth={1}
        borderColor={open || highlight ? '$primary' : '$borderColor'}
        borderRadius={7}
        opacity={disabled ? 0.6 : 1}
        role="button"
        aria-disabled={!!disabled}
        aria-expanded={open}
      >
        <Text flex={1} fontSize={14} color={value ? '$color' : '$tertiary'}>
          {value || placeholder}
        </Text>
        <Icon name="clock" size={16} color="$muted" />
      </XStack>

      {open && !disabled && (
        <>
          <Backdrop onPress={() => setOpen(false)} />
          <YStack
            position="absolute"
            top={46}
            {...(align === 'left' ? { left: 0 } : { right: 0 })}
            minWidth={180}
            zIndex={50}
            padding={8}
            borderWidth={1}
            borderRadius={12}
            backgroundColor="$bgElevated"
            borderColor="$borderSecondary"
            shadowColor={palette.shadow}
            shadowOpacity={0.18}
            shadowRadius={20}
            shadowOffset={{ width: 0, height: 14 }}
          >
            <Stack height={VIEW_H} position="relative">
              {/* centre highlight band — behind the columns */}
              <Stack
                position="absolute"
                left={4}
                right={4}
                top={PAD}
                height={ITEM_H}
                backgroundColor="$backgroundHover"
                borderRadius={8}
                zIndex={0}
                pointerEvents="none"
              />
              <XStack position="relative" zIndex={1} justifyContent="center" alignItems="center" gap={10}>
                <WheelColumn
                  values={hours}
                  value={h}
                  onChange={(nh) => {
                    // landing on the bound hour: lift the minute to the first allowed one
                    const floor = bound && nh === bound[0] ? bound[1] + 1 : 0
                    onChange(`${pad2(nh)}:${pad2(Math.max(m, floor))}`)
                  }}
                />
                <Text fontSize={16} fontWeight="500" color="$muted">
                  :
                </Text>
                <WheelColumn key={`m${minuteFloor}`} values={minutes} value={m} onChange={(nm) => onChange(`${pad2(h)}:${pad2(nm)}`)} />
              </XStack>
            </Stack>
          </YStack>
        </>
      )}
    </Stack>
  )
}
